import cookie from 'js-cookie';

import { log } from '~/utils/log';
import { getPageViewId } from '~/utils/navigationContext';

const SCHEMA_VERSION = 1;

export type InteractionTargetType =
  | 'advert'
  | 'external_text_link'
  | 'product_card'
  | 'raffle_entry'
  | 'retailer'
  | 'sort';

export type InteractionServerEvent<T = Record<string, unknown>> = {
  data: {
    client_ts: number;
    gesture?: string;
    guest_id?: string;
    schema_version: number;
    target_properties: T;
    target_type: InteractionTargetType;
    view_id: string;
  };
  type: 'interaction';
};

type BaseData = {
  gesture?: string;
  guestId?: string;
  isBot: boolean;
};

abstract class InteractionEvent<
  D extends BaseData = BaseData,
  T extends Record<string, unknown> = Record<string, unknown>
> {
  constructor(protected data: D) {}

  protected abstract get targetType(): InteractionTargetType;

  async send() {
    if (this.data.isBot) return;

    const event = this.buildEvent();

    if (!event) return;

    return fetch('/bolt/event', {
      body: JSON.stringify({ events: [event] }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST'
    });
  }

  private buildEvent(): InteractionServerEvent<T> | undefined {
    const viewId = getPageViewId();

    if (!viewId) {
      log.error('InteractionEvent fired without a view_id', {
        target_type: this.targetType
      });

      return;
    }

    return {
      data: {
        client_ts: Date.now(),
        gesture: this.data.gesture,
        guest_id: this.data.guestId ?? cookie.get('clickRefUid'),
        schema_version: SCHEMA_VERSION,
        target_properties: this.getTargetProperties(),
        target_type: this.targetType,
        view_id: viewId
      },
      type: 'interaction'
    };
  }

  protected abstract getTargetProperties(): T;
}

export default InteractionEvent;
